import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, ArrowRight, Mail, Phone, MapPin, Linkedin, Building2, Clock } from 'lucide-react';
interface NavItem {
  label: string;
  to: string;
  description: string;
}
interface NavGroup {
  label: string;
  blurb: string;
  items: NavItem[];
}
const NAV_GROUPS: NavGroup[] = [{
  label: 'Profile',
  blurb: 'The leader, her journey, and her guiding philosophy.',
  items: [{
    label: 'About Michelle',
    to: '/about',
    description: 'Executive biography & leadership values'
  }, {
    label: 'Resume & Credentials',
    to: '/credentials',
    description: 'Education, certifications & competencies'
  }]
}, {
  label: 'Experience',
  blurb: 'Two decades of executive healthcare leadership.',
  items: [{
    label: 'Executive Experience',
    to: '/experience',
    description: 'Interactive career timeline'
  }, {
    label: 'Board Service & Governance',
    to: '/board',
    description: 'Board leadership & fiduciary oversight'
  }, {
    label: 'Success Stories',
    to: '/success-stories',
    description: 'Leadership case studies'
  }]
}, {
  label: 'Impact',
  blurb: 'Advocacy, transformation, and thought leadership.',
  items: [{
    label: 'Healthcare Leadership & Impact',
    to: '/leadership',
    description: 'Advocacy & transformation initiatives'
  }, {
    label: 'Speaking & Thought Leadership',
    to: '/speaking',
    description: 'Keynotes & event inquiries'
  }, {
    label: 'Media & Press',
    to: '/media',
    description: 'Interviews, articles & features'
  }]
}];
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveMegaMenu(null);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return <div className="min-h-screen flex flex-col bg-slate-50" data-id="element-23">
      {/* Utility bar */}
      <div className="bg-navy-950 text-white/80 py-2 px-6 text-sm hidden md:flex justify-between items-center z-50 relative" data-id="element-24">
        <div className="flex items-center space-x-6" data-id="element-25">
          <span className="flex items-center" data-id="element-26">
            <Building2 className="w-4 h-4 mr-2 text-gold" data-id="element-27" />
            Office of Michelle McOmber
          </span>
          <span className="flex items-center" data-id="element-28">
            <Clock className="w-4 h-4 mr-2 text-gold" data-id="element-29" />
            Welcoming national executive & board opportunities
          </span>
        </div>
        <div className="flex items-center space-x-4" data-id="element-30">
          <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn profile" className="hover:text-gold transition-colors" data-id="element-31">
            <Linkedin className="w-4 h-4" data-id="element-32" />
          </a>
        </div>
      </div>

      {/* Primary navigation */}
      <header onMouseLeave={() => setActiveMegaMenu(null)} className={`sticky top-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white shadow-executive py-3' : 'bg-white/95 backdrop-blur py-5'}`} data-id="element-33">
        <nav className="max-w-7xl mx-auto px-6 flex justify-between items-center" aria-label="Primary" data-id="element-34">
          <Link to="/" className="flex flex-col z-50 relative" data-id="element-35">
            <span className="font-serif text-2xl md:text-3xl font-bold text-navy-900 leading-tight" data-id="element-36">
              Michelle McOmber
            </span>
            <span className="text-xs tracking-[0.2em] text-gold uppercase font-semibold mt-1" data-id="element-37">
              Healthcare Executive
            </span>
          </Link>

          <div className="hidden lg:flex items-center space-x-8" data-id="element-38">
            <Link to="/" className={`text-sm font-medium transition-colors ${location.pathname === '/' ? 'text-gold' : 'text-navy-900 hover:text-gold'}`} data-id="element-39">
              Home
            </Link>
            {NAV_GROUPS.map(group => <button key={group.label} onMouseEnter={() => setActiveMegaMenu(group.label)} onFocus={() => setActiveMegaMenu(group.label)} aria-expanded={activeMegaMenu === group.label} className="text-sm font-medium text-navy-900 hover:text-gold transition-colors flex items-center" data-id="element-40">
                {group.label}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-41" />
              </button>)}
            <Link to="/contact" className="bg-navy-900 text-white px-6 py-2.5 text-sm font-medium hover:bg-navy-800 transition-colors" data-id="element-42">
              Schedule a Conversation
            </Link>
          </div>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle navigation menu" className="lg:hidden text-navy-900 z-50 relative" data-id="element-43">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-44" /> : <Menu className="w-6 h-6" data-id="element-45" />}
          </button>
        </nav>

        {/* Mega menu */}
        <AnimatePresence data-id="element-46">
          {activeMegaMenu && <motion.div initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.18
        }} onMouseEnter={() => setActiveMegaMenu(activeMegaMenu)} className="absolute left-0 right-0 top-full bg-white border-t border-slate-100 shadow-executive hidden lg:block" data-id="element-47">
              <div className="max-w-7xl mx-auto px-6 py-10" data-id="element-48">
                {NAV_GROUPS.filter(g => g.label === activeMegaMenu).map(group => <div key={group.label} className="grid grid-cols-4 gap-8" data-id="element-49">
                      <div className="col-span-1 border-r border-slate-100 pr-8" data-id="element-50">
                        <h3 className="font-serif text-xl text-navy-900 mb-2" data-id="element-51">
                          {group.label}
                        </h3>
                        <p className="text-sm text-slate-500" data-id="element-52">{group.blurb}</p>
                      </div>
                      <div className="col-span-3 grid grid-cols-2 gap-x-12 gap-y-6" data-id="element-53">
                        {group.items.map(item => <Link key={item.to} to={item.to} onClick={() => setActiveMegaMenu(null)} className="group block" data-id="element-54">
                            <div className="flex items-center justify-between mb-1" data-id="element-55">
                              <span className="font-medium text-navy-900 group-hover:text-gold transition-colors" data-id="element-56">
                                {item.label}
                              </span>
                              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-gold transition-colors transform group-hover:translate-x-1" data-id="element-57" />
                            </div>
                            <p className="text-sm text-slate-500" data-id="element-58">
                              {item.description}
                            </p>
                          </Link>)}
                      </div>
                    </div>)}
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Mobile menu */}
        <AnimatePresence data-id="element-59">
          {mobileMenuOpen && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="lg:hidden fixed inset-0 top-0 bg-white z-40 pt-24 overflow-y-auto" data-id="element-60">
              <div className="px-6 pb-20 space-y-8" data-id="element-61">
                <Link to="/" className="block text-lg font-medium text-navy-900" data-id="element-62">
                  Home
                </Link>
                {NAV_GROUPS.map(group => <div key={group.label} className="space-y-4" data-id="element-63">
                    <p className="text-xs font-bold tracking-widest text-gold uppercase" data-id="element-64">
                      {group.label}
                    </p>
                    <div className="space-y-3 pl-4 border-l-2 border-slate-100" data-id="element-65">
                      {group.items.map(item => <Link key={item.to} to={item.to} className="block text-slate-600 hover:text-navy-900" data-id="element-66">
                          {item.label}
                        </Link>)}
                    </div>
                  </div>)}
                <Link to="/contact" className="block bg-navy-900 text-white px-6 py-3 text-center font-medium" data-id="element-67">
                  Schedule a Conversation
                </Link>
              </div>
            </motion.div>}
        </AnimatePresence>
      </header>

      <main className="flex-grow" data-id="element-68">{children}</main>

      {/* Footer */}
      <footer className="bg-navy-950 text-white pt-20 pb-10" data-id="element-69">
        <div className="max-w-7xl mx-auto px-6" data-id="element-70">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16" data-id="element-71">
            <div className="lg:col-span-2" data-id="element-72">
              <span className="font-serif text-3xl font-bold text-white block mb-2" data-id="element-73">
                Michelle McOmber
              </span>
              <span className="text-gold uppercase tracking-widest text-sm font-semibold block mb-6" data-id="element-74">
                Healthcare Executive
              </span>
              <p className="text-slate-400 max-w-md leading-relaxed" data-id="element-75">
                Transforming healthcare through leadership, advocacy, and
                strategic innovation. Available for executive, board, advisory,
                and speaking engagements nationwide.
              </p>
            </div>

            <div data-id="element-76">
              <h4 className="font-serif text-lg mb-6" data-id="element-77">Quick Links</h4>
              <ul className="space-y-3 text-slate-400" data-id="element-78">
                <li data-id="element-79">
                  <Link to="/about" className="hover:text-gold transition-colors" data-id="element-80">
                    Biography
                  </Link>
                </li>
                <li data-id="element-81">
                  <Link to="/experience" className="hover:text-gold transition-colors" data-id="element-82">
                    Experience
                  </Link>
                </li>
                <li data-id="element-83">
                  <Link to="/board" className="hover:text-gold transition-colors" data-id="element-84">
                    Board Service
                  </Link>
                </li>
                <li data-id="element-85">
                  <Link to="/contact" className="hover:text-gold transition-colors" data-id="element-86">
                    Contact Office
                  </Link>
                </li>
              </ul>
            </div>

            <div data-id="element-87">
              <h4 className="font-serif text-lg mb-6" data-id="element-88">Connect</h4>
              <ul className="space-y-4 text-slate-400" data-id="element-89">
                <li className="flex items-center" data-id="element-90">
                  <Mail className="w-5 h-5 mr-3 text-gold" data-id="element-91" />
                  michelle@mcomber.example
                </li>
                <li className="flex items-center" data-id="element-92">
                  <Phone className="w-5 h-5 mr-3 text-gold" data-id="element-93" />
                  (801) 555-0198
                </li>
                <li className="flex items-center" data-id="element-94">
                  <MapPin className="w-5 h-5 mr-3 text-gold" data-id="element-95" />
                  Salt Lake City, Utah
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-slate-500" data-id="element-96">
            <p data-id="element-97">
              &copy; {new Date().getFullYear()} Michelle McOmber. All rights
              reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0" data-id="element-98">
              <Link to="/leadership" className="hover:text-white transition-colors" data-id="element-99">
                Healthcare Policy
              </Link>
              <Link to="/board" className="hover:text-white transition-colors" data-id="element-100">
                Board Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}